import React, { useEffect, useMemo, useState } from "react"

export type DateTimePickerEvent = {
  type: "set" | "dismissed"
}

type DateTimePickerProps = {
  value: Date
  mode?: "date" | "time" | "datetime"
  display?: "default" | "spinner" | "calendar" | "clock"
  minimumDate?: Date
  maximumDate?: Date
  onChange?: (event: DateTimePickerEvent, date?: Date) => void
  style?: React.CSSProperties
}

function toInputValue(date: Date) {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, "0")
  const d = String(date.getDate()).padStart(2, "0")
  return `${y}-${m}-${d}`
}

function fromInputValue(value: string) {
  const [y, m, d] = value.split("-").map(Number)
  return new Date(y, m - 1, d, 12, 0, 0, 0)
}

function clampDate(date: Date, min?: Date, max?: Date) {
  const time = date.getTime()
  if (min && time < min.getTime()) return new Date(min.getTime())
  if (max && time > max.getTime()) return new Date(max.getTime())
  return date
}

const DateTimePicker: React.FC<DateTimePickerProps> = ({
  value,
  minimumDate,
  maximumDate,
  onChange,
  style,
}) => {
  const [inner, setInner] = useState(() => toInputValue(value))

  useEffect(() => {
    setInner(toInputValue(value))
  }, [value])

  const min = minimumDate ? toInputValue(minimumDate) : undefined
  const max = maximumDate ? toInputValue(maximumDate) : undefined

  const years = useMemo(() => {
    const start = minimumDate?.getFullYear() ?? value.getFullYear() - 50
    const end = maximumDate?.getFullYear() ?? value.getFullYear() + 10
    const list: number[] = []
    for (let y = start; y <= end; y++) list.push(y)
    return list
  }, [maximumDate, minimumDate, value])

  const parsed = useMemo(() => {
    const [y, m, d] = inner.split("-").map(Number)
    return { y, m, d }
  }, [inner])

  const daysInMonth = useMemo(
    () => new Date(parsed.y, parsed.m, 0).getDate(),
    [parsed.m, parsed.y],
  )

  const months = useMemo(() => Array.from({ length: 12 }, (_, i) => i + 1), [])
  const days = useMemo(
    () => Array.from({ length: daysInMonth }, (_, i) => i + 1),
    [daysInMonth],
  )

  const emit = (nextValue: string) => {
    if (!nextValue) return
    const next = clampDate(fromInputValue(nextValue), minimumDate, maximumDate)
    const normalized = toInputValue(next)
    setInner(normalized)
    onChange?.({ type: "set" }, next)
  }

  const updatePart = (part: "y" | "m" | "d", raw: number) => {
    let { y, m, d } = parsed
    if (part === "y") y = raw
    if (part === "m") m = raw
    if (part === "d") d = raw
    const maxDay = new Date(y, m, 0).getDate()
    if (d > maxDay) d = maxDay
    emit(
      `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`,
    )
  }

  const selectStyle: React.CSSProperties = {
    flex: 1,
    minWidth: 0,
    height: 180,
    fontSize: 18,
    border: "none",
    background: "transparent",
    textAlign: "center",
    outline: "none",
    cursor: "pointer",
  }

  return (
    <div
      style={{
        display: "flex",
        width: "100%",
        gap: 8,
        alignItems: "stretch",
        ...style,
      }}
    >
      <select
        aria-label="年"
        value={parsed.y}
        style={selectStyle}
        onChange={(e) => updatePart("y", Number(e.target.value))}
      >
        {years.map((year) => (
          <option key={year} value={year}>
            {year}年
          </option>
        ))}
      </select>
      <select
        aria-label="月"
        value={parsed.m}
        style={selectStyle}
        onChange={(e) => updatePart("m", Number(e.target.value))}
      >
        {months.map((month) => (
          <option key={month} value={month}>
            {month}月
          </option>
        ))}
      </select>
      <select
        aria-label="日"
        value={parsed.d}
        style={selectStyle}
        onChange={(e) => updatePart("d", Number(e.target.value))}
      >
        {days.map((day) => (
          <option key={day} value={day}>
            {day}日
          </option>
        ))}
      </select>
      <input type="hidden" value={inner} min={min} max={max} readOnly />
    </div>
  )
}

export const DateTimePickerAndroid = {
  open: (opts: {
    value?: Date
    mode?: string
    minimumDate?: Date
    maximumDate?: Date
    title?: string
    onChange: (event: DateTimePickerEvent, date?: Date) => void
  }) => {
    const picked = opts.value ?? new Date()
    opts.onChange({ type: "set" }, picked)
  },
}

export default DateTimePicker
